import styled from 'styled-components';

interface CircleProps {
  radius: number;
  stroke: number;
  percent: number;
  color: string;
  appClassnamePrefix?: string;
}

const Circle = ({
  radius,
  stroke,
  percent,
  color,
  appClassnamePrefix = ''
}: CircleProps) => {
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <ProgressSVGComponent
      _appClassPrefix={appClassnamePrefix}
      height={radius * 2}
      width={radius * 2}
    >
      <ProgressCircle
        _appClassPrefix={appClassnamePrefix}
        stroke={color || 'transparent'}
        strokeDasharray={`${circumference} ${circumference}`}
        style={{
          strokeDashoffset: offset
        }}
        strokeWidth={stroke}
        fill="transparent"
        r={normalizedRadius}
        cx={radius}
        cy={radius}
      />
    </ProgressSVGComponent>
  );
};

interface ProgressCircleProps {
  _appClassPrefix: string;
}

const ProgressCircle = styled.circle.attrs<ProgressCircleProps>(
  ({ _appClassPrefix }) => ({
    className: `${_appClassPrefix}-progress-circle`
  })
)`
  transition: stroke-dashoffset 0s;
  transform-origin: 50% 50%;
  transform: rotate(-90deg);
`;

interface LineProps {
  percent: number;
  height?: number;
  appClassnamePrefix?: string;
}

const Line = ({ percent, height = 1, appClassnamePrefix = '' }: LineProps) => (
  <ProgressLine
    _appClassPrefix={appClassnamePrefix}
    _height={height}
    _widthPercent={percent}
  />
);

interface ProgressLineProps {
  _appClassPrefix: string;
  _widthPercent: number;
  _height: number;
}

const ProgressLine = styled.div.attrs<ProgressLineProps>(
  ({ _appClassPrefix }) => ({
    className: `${_appClassPrefix}-progress-line`
  })
)`
  ${({ _widthPercent, _height }) => `
    width: ${_widthPercent}%;
    height: ${_height}px;
  `}
`;

interface CapsuleProps {
  width: number;
  height: number;
  stroke: number;
  percent: number;
  color: string;
  appClassnamePrefix?: string;
}

const LINE_LENGTH = 100;
const LINE_CONNECTION_CORRECTION = 1;

const Capsule = ({
  width,
  height,
  stroke,
  percent,
  color,
  appClassnamePrefix = ''
}: CapsuleProps) => {
  const componentHeight = height + stroke * 4;
  const componentWidth = width + stroke * 4;
  const offsetCorrection = (LINE_CONNECTION_CORRECTION * (percent - 50)) / 100;
  const offset = -percent - offsetCorrection;

  const pathWidth = width + stroke * 2;
  const pathHeight = height + stroke * 2;
  const halfPathWidth = pathWidth / 2;
  const halfPathHeight = pathHeight / 2;

  const pathDefinition = [
    `M ${halfPathWidth} 0`,
    `L ${pathWidth - halfPathHeight} 0`,
    `A 1 1 1 0 1 ${pathWidth - halfPathHeight} ${pathHeight}`,
    `L ${halfPathHeight} ${pathHeight}`,
    `A 1 1 1 0 1 ${halfPathHeight} 0`,
    `Z`
  ].join(' ');

  return (
    <ProgressSVGComponent
      _appClassPrefix={appClassnamePrefix}
      height={componentHeight}
      width={componentWidth}
    >
      <path
        d={pathDefinition}
        stroke={color || 'transparent'}
        pathLength={LINE_LENGTH}
        strokeDasharray={LINE_LENGTH + LINE_CONNECTION_CORRECTION}
        strokeDashoffset={offset}
        strokeWidth={stroke}
        fill="none"
        transform={`translate(${stroke}, ${stroke})`}
      />
    </ProgressSVGComponent>
  );
};

type ProgressSVGComponent = {
  _appClassPrefix: string;
};

const ProgressSVGComponent = styled.svg.attrs<ProgressSVGComponent>(
  ({ _appClassPrefix }) => ({
    className: `${_appClassPrefix}-progress-component`
  })
)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

export const Progress = { Circle, Line, Capsule };
